import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  RadioButtonUnchecked as OptionIcon
} from '@mui/icons-material';
import MultilingualInput from './MultilingualInput';
import MarkdownEditorMultilingual from './MarkdownEditorMultilingual';
import { generateClientId } from '../utils/quizIds';

export interface IQuizOption {
  id: string;
  [key: string]: string;
}

export interface IQuizQuestion {
  id: string;
  question: { [key: string]: string };
  options: IQuizOption[];
  correctAnswerIndex?: number;
  order: number;
}

export interface IQuiz {
  order: number;
  title: { [key: string]: string };
  description?: { [key: string]: string };
  details?: { [key: string]: string };
  questions: IQuizQuestion[];
  passingPercentage?: number;
  isDiagnostic: boolean;
}

interface QuizEditorProps {
  quiz: IQuiz;
  onChange: (quiz: IQuiz) => void;
  onDelete?: () => void;
  availableLanguages: string[];
  isOpen?: boolean;
}

const confirmQuizDelete = (message: string): boolean => window.confirm(message);

const QuizEditor: React.FC<QuizEditorProps> = ({
  quiz,
  onChange,
  onDelete,
  availableLanguages,
  isOpen = true
}) => {
  const [activeTab, setActiveTab] = React.useState<'info' | 'questions'>('info');

  React.useEffect(() => {
    if (isOpen) {
      setActiveTab('info');
    }
  }, [isOpen]);

  const insertQuestionAt = (insertIndex: number) => {
    const baseOrder = insertIndex + 1;
    const newQuestion: IQuizQuestion = {
      id: generateClientId('q'),
      question: {},
      options: [
        { id: generateClientId('opt') },
        { id: generateClientId('opt') },
        { id: generateClientId('opt') },
        { id: generateClientId('opt') },
      ],
      order: baseOrder
    };
    const questions = [...quiz.questions];
    questions.splice(insertIndex, 0, newQuestion);
    const reOrdered = questions.map((q, idx) => ({ ...q, order: idx + 1 }));
    onChange({ ...quiz, questions: reOrdered });
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    const questions = [...quiz.questions];
    if (direction === 'up' && index > 0) {
      [questions[index - 1], questions[index]] = [questions[index], questions[index - 1]];
    } else if (direction === 'down' && index < questions.length - 1) {
      [questions[index], questions[index + 1]] = [questions[index + 1], questions[index]];
    } else {
      return;
    }
    const reOrdered = questions.map((q, idx) => ({ ...q, order: idx + 1 }));
    onChange({ ...quiz, questions: reOrdered });
  };
  const updateQuestion = (questionId: string, updates: Partial<IQuizQuestion>) => {
    onChange({
      ...quiz,
      questions: quiz.questions.map(q => 
        q.id === questionId ? { ...q, ...updates } : q
      )
    });
  };

  const deleteQuestion = (questionId: string) => {
    if (
      !confirmQuizDelete(
        'Supprimer cette question ? Les réponses associées ne seront plus disponibles dans le quiz.'
      )
    ) {
      return;
    }
    const questions = quiz.questions
      .filter((q) => q.id !== questionId)
      .map((q, idx) => ({ ...q, order: idx + 1 }));
    onChange({ ...quiz, questions });
  };

  const addOption = (questionId: string) => {
    onChange({
      ...quiz,
      questions: quiz.questions.map(q => 
        q.id === questionId ? { ...q, options: [...q.options, { id: generateClientId('opt') }] } : q
      )
    });
  };

  const updateOption = (questionId: string, optionId: string, value: { [key: string]: string }) => {
    onChange({
      ...quiz,
      questions: quiz.questions.map(q => 
        q.id === questionId ? {
          ...q,
          options: q.options.map((opt) => opt.id === optionId ? { ...opt, ...value, id: opt.id } : opt)
        } : q
      )
    });
  };

  const deleteOption = (questionId: string, optionId: string) => {
    if (
      !confirmQuizDelete(
        'Supprimer cette option de réponse ? Cette action est irréversible.'
      )
    ) {
      return;
    }
    onChange({
      ...quiz,
      questions: quiz.questions.map(q => {
        if (q.id !== questionId) return q;
        const removedIndex = q.options.findIndex((opt) => opt.id === optionId);
        if (removedIndex < 0) return q;
        const nextOptions = q.options.filter((opt) => opt.id !== optionId);
        let nextCorrectIndex = q.correctAnswerIndex;
        if (nextCorrectIndex !== undefined) {
          if (removedIndex === nextCorrectIndex) {
            nextCorrectIndex = undefined;
          } else if (removedIndex < nextCorrectIndex) {
            nextCorrectIndex -= 1;
          }
        }
        return { ...q, options: nextOptions, correctAnswerIndex: nextCorrectIndex };
      })
    });
  };

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2 }}
      >
        <Tab label="Informations" value="info" />
        <Tab label="Questions" value="questions" />
      </Tabs>

      {activeTab === 'info' && (
        <>
          <MultilingualInput
            label="Titre du quiz"
            value={quiz.title}
            onChange={(value) => onChange({ ...quiz, title: value })}
            availableLanguages={availableLanguages}
            required
          />

          <MultilingualInput
            label="Label"
            value={quiz.description || {}}
            onChange={(value) => onChange({ ...quiz, description: value })}
            availableLanguages={availableLanguages}
          />

          <Box sx={{ mt: 2 }}>
            <MarkdownEditorMultilingual
              label="Détails du quiz"
              value={quiz.details || {}}
              onChange={(value) => onChange({ ...quiz, details: value })}
              availableLanguages={availableLanguages}
              placeholder="Saisissez les détails du quiz (HTML)..."
              height={280}
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={quiz.isDiagnostic}
                onChange={(e) => onChange({ ...quiz, isDiagnostic: e.target.checked })}
              />
            }
            label="Quiz diagnostique (sans notation)"
          />

          {!quiz.isDiagnostic && (
            <TextField
              label="Pourcentage de réussite (%)"
              type="number"
              value={quiz.passingPercentage || ''}
              onChange={(e) => onChange({ ...quiz, passingPercentage: Number(e.target.value) })}
              size="small"
              sx={{ mt: 1, mb: 2, width: 220 }}
              inputProps={{ min: 0, max: 100 }}
            />
          )}
        </>
      )}

      {activeTab === 'questions' && (
        <>
          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 1 }}>
            Questions
          </Typography>

          <Box display="flex" justifyContent="center" mb={1.5}>
            <Tooltip title="Ajouter une question">
              <IconButton
                size="small"
                onClick={() => insertQuestionAt(0)}
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: 'grey.50',
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {quiz.questions.map((question, qIndex) => (
            <React.Fragment key={question.id}>
              <Card
                sx={{
                  mb: 0.75,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.600',
                  borderRadius: 2,
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, fontSize: '1.05rem' }}
                    >
                      Question {qIndex + 1}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Tooltip title="Monter la question">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => moveQuestion(qIndex, 'up')}
                            disabled={qIndex === 0}
                          >
                            <ArrowUpIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Descendre la question">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => moveQuestion(qIndex, 'down')}
                            disabled={qIndex === quiz.questions.length - 1}
                          >
                            <ArrowDownIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Supprimer la question">
                        <IconButton size="small" onClick={() => deleteQuestion(question.id)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <MultilingualInput
                    label=""
                    value={question.question}
                    onChange={(value) => updateQuestion(question.id, { question: value })}
                    availableLanguages={availableLanguages}
                    required
                  />

                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={1}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Options (min 2)
                    </Typography>
                    <Button size="small" onClick={() => addOption(question.id)}>
                      Ajouter une option
                    </Button>
                  </Box>

                  {question.options.map((option, optIndex) => (
                    <Box key={option.id} display="flex" alignItems="center" gap={1} mb={0.5}>
                      <OptionIcon fontSize="small" sx={{ color: 'text.disabled', flexShrink: 0 }} />
                      <Box flexGrow={1}>
                        <MultilingualInput
                          label=""
                          value={option}
                          onChange={(value) => updateOption(question.id, option.id, value)}
                          availableLanguages={availableLanguages}
                          required
                        />
                      </Box>
                      {!quiz.isDiagnostic && (
                        <Button
                          variant={question.correctAnswerIndex === optIndex ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => updateQuestion(question.id, { correctAnswerIndex: optIndex })}
                          sx={{ minWidth: '80px' }}
                        >
                          {question.correctAnswerIndex === optIndex ? 'Correcte' : 'Marquer'}
                        </Button>
                      )}
                      {question.options.length > 2 && (
                        <IconButton size="small" onClick={() => deleteOption(question.id, option.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </CardContent>
              </Card>

              <Box display="flex" justifyContent="center" mb={0.75}>
                <Tooltip title="Ajouter une question">
                  <IconButton
                    size="small"
                    onClick={() => insertQuestionAt(qIndex + 1)}
                    sx={{
                      width: 30,
                      height: 30,
                      bgcolor: 'grey.50',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                      '&:hover': { bgcolor: 'grey.100' }
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </React.Fragment>
          ))}
        </>
      )}

      {onDelete && (
        <Button variant="outlined" color="error" onClick={onDelete} size="small" sx={{ mt: 2 }}>
          Supprimer le quiz
        </Button>
      )}
    </Box>
  );
};

export default QuizEditor;

